/**
 * Wer ist angemeldet.
 *
 * Im Browser lebt das Token ausschließlich im HttpOnly-Cookie — für
 * JavaScript unerreichbar. Die Oberfläche kann also nicht selbst
 * nachsehen, ob sie angemeldet ist; sie fragt beim Start einmal
 * `/api/ich`. Solange diese Antwort aussteht, wird noch nichts entschieden:
 * Wer zu früh auf die Anmeldemaske umschaltet, wirft bei jedem Neuladen
 * kurz einen Anmeldedialog auf einen Benutzer, der längst angemeldet ist.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { api, SITZUNG_ENDE } from '../lib/api';

export type Rolle = 'inhaber' | 'steuerberater' | 'lesen';

export type Benutzer = {
  id: string;
  name: string;
  email: string;
  rolle: Rolle;
  unternehmenId: string;
};

type Sitzung = {
  benutzer: Benutzer | null;
  /** Der erste Blick auf /api/ich steht noch aus. */
  wirdGeprueft: boolean;
  darfSchreiben: boolean;
  anmelden: (email: string, passwort: string, code?: string) => Promise<void>;
  abmelden: () => Promise<void>;
  neuLaden: () => Promise<void>;
};

const Kontext = createContext<Sitzung | null>(null);

export function SitzungAnbieter({ children }: { children: ReactNode }): ReactNode {
  const [benutzer, setzeBenutzer] = useState<Benutzer | null>(null);
  const [wirdGeprueft, setzeWirdGeprueft] = useState(true);

  const pruefe = useCallback(async () => {
    try {
      setzeBenutzer(await api.hole<Benutzer>('/api/ich'));
    } catch {
      // Ein 401 ist hier der Normalfall: niemand angemeldet.
      setzeBenutzer(null);
    } finally {
      setzeWirdGeprueft(false);
    }
  }, []);

  useEffect(() => {
    void pruefe();
  }, [pruefe]);

  // Läuft die Sitzung irgendwo mitten in der Anwendung ab, meldet die
  // API-Schicht das einmal — hier wird daraus die Anmeldemaske.
  useEffect(() => {
    const beiEnde = (): void => setzeBenutzer(null);
    window.addEventListener(SITZUNG_ENDE, beiEnde);
    return () => window.removeEventListener(SITZUNG_ENDE, beiEnde);
  }, []);

  const anmelden = useCallback(async (email: string, passwort: string, code?: string) => {
    const antwort = await api.senden<{ benutzer: Omit<Benutzer, 'unternehmenId'> }>(
      '/api/anmeldung',
      { email, passwort, ...(code ? { code } : {}), typ: 'web' },
    );
    // Die Anmeldeantwort enthält das Unternehmen nicht — sie kennt den
    // Benutzer, nicht seinen Mandanten. Statt das Feld zu erfinden, wird
    // der vollständige Datensatz von /api/ich geholt.
    void antwort;
    await api.hole<Benutzer>('/api/ich').then(setzeBenutzer);
  }, []);

  const abmelden = useCallback(async () => {
    try {
      await api.senden('/api/abmeldung');
    } finally {
      // Auch wenn der Aufruf scheitert: Auf diesem Gerät ist Schluss.
      setzeBenutzer(null);
    }
  }, []);

  const wert = useMemo<Sitzung>(
    () => ({
      benutzer,
      wirdGeprueft,
      darfSchreiben: benutzer !== null && benutzer.rolle !== 'lesen',
      anmelden,
      abmelden,
      neuLaden: pruefe,
    }),
    [benutzer, wirdGeprueft, anmelden, abmelden, pruefe],
  );

  return <Kontext.Provider value={wert}>{children}</Kontext.Provider>;
}

export function useSitzung(): Sitzung {
  const kontext = useContext(Kontext);
  if (!kontext) throw new Error('useSitzung außerhalb des SitzungAnbieters verwendet');
  return kontext;
}
